import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { WarningBadge } from "@/components/Badges";
import { ListX, Skull, AlertTriangle, ShieldCheck, Building2, FileStack, Users, TrendingUp, LayoutDashboard } from "lucide-react";

function StatCard({ icon: Icon, label, value, accent, testid }) {
  return (
    <div
      data-testid={testid}
      className={`bg-card border rounded-sm p-5 relative overflow-hidden ${accent ? "border-primary/40" : "border-zinc-800"}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-500">{label}</div>
          <div className="font-display text-4xl font-black text-white mt-2 tracking-tight">{value}</div>
        </div>
        <div className={`p-2 rounded-sm ${accent ? "bg-primary/15 text-primary" : "bg-white/5 text-zinc-400"}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      <div className="mb-8">
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-2">Tổng quan</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter text-white">
          Xin chào, {user?.company_name || user?.name}
        </h1>
        <p className="text-zinc-500 mt-2">Theo dõi tình hình cảnh báo khách hàng rủi ro.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard testid="stat-total" icon={ListX} label="Tổng danh sách đen" value={stats?.total_blacklist ?? "—"} />
        <StatCard testid="stat-danger" icon={Skull} label="Đặc biệt nguy hiểm" value={stats?.danger ?? "—"} accent />
        <StatCard testid="stat-warning" icon={AlertTriangle} label="Mức cảnh báo" value={stats?.warning ?? "—"} />
        {user?.role === "root" && (
          <StatCard testid="stat-pending-reports" icon={ShieldCheck} label="Báo cáo chờ duyệt" value={stats?.pending_reports ?? "—"} accent />
        )}
        {user?.role === "root" && (
          <StatCard testid="stat-pending-companies" icon={Building2} label="Nhà xe chờ duyệt" value={stats?.pending_companies ?? "—"} />
        )}
        {user?.role === "root" && (
          <StatCard testid="stat-total-companies" icon={Building2} label="Nhà xe hoạt động" value={stats?.total_companies ?? "—"} />
        )}
        {user?.role === "company" && (
          <StatCard testid="stat-my-reports" icon={FileStack} label="Báo cáo của tôi" value={stats?.my_reports ?? "—"} />
        )}
        {user?.role === "company" && (
          <StatCard testid="stat-my-employees" icon={Users} label="Nhân viên" value={stats?.my_employees ?? "—"} />
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-card border border-zinc-800 rounded-sm p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-display text-xl font-bold text-white flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" /> Cảnh báo mới nhất
            </h2>
            <Link to="/danh-sach-den" className="text-sm text-primary hover:text-[#ff1a1a] font-semibold">
              Xem tất cả
            </Link>
          </div>
          {stats?.recent?.length ? (
            <div className="divide-y divide-zinc-800">
              {stats.recent.map((r) => (
                <Link
                  key={r.id}
                  to={`/danh-sach-den/${r.id}`}
                  data-testid={`recent-${r.id}`}
                  className="flex items-center justify-between py-3 hover:bg-white/5 -mx-2 px-2 rounded-sm transition-colors"
                >
                  <div>
                    <div className="font-semibold text-white">{r.customer_name}</div>
                    <div className="text-xs text-zinc-500">
                      CCCD: {r.cccd_number || "—"} · {r.reported_by_name}
                    </div>
                  </div>
                  <WarningBadge level={r.warning_level} />
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-zinc-600 text-sm py-6 text-center">Chưa có dữ liệu.</p>
          )}
        </div>

        <div data-testid="top-offenders" className="bg-card border border-red-500/30 rounded-sm p-6">
          <h2 className="font-display text-xl font-bold text-white flex items-center gap-2 mb-5">
            <Skull className="w-5 h-5 text-primary" /> Khách nguy hiểm nhất
          </h2>
          {stats?.top_offenders?.length ? (
            <div className="space-y-3">
              {stats.top_offenders.map((r, i) => (
                <Link
                  key={r.id}
                  to={`/danh-sach-den/${r.id}`}
                  data-testid={`offender-${r.id}`}
                  className="flex items-center gap-3 p-3 bg-red-500/5 border border-red-500/20 hover:border-red-500/60 rounded-sm transition-colors"
                >
                  <div className="w-7 h-7 shrink-0 bg-primary text-white font-display font-black flex items-center justify-center rounded-sm text-sm">
                    {i + 1}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-white truncate">{r.customer_name}</div>
                    <div className="text-xs text-zinc-500">CCCD: {r.cccd_number || "—"}</div>
                  </div>
                  <div className="flex items-center gap-1 text-red-400 font-black text-sm shrink-0">
                    <AlertTriangle className="w-4 h-4" /> {r.report_count}x
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-zinc-600 text-sm py-6 text-center">Chưa có khách nào bị báo cáo nhiều lần.</p>
          )}
        </div>
      </div>
    </div>
  );
}
